/* eslint-disable @typescript-eslint/no-explicit-any*/
import { DSLErrorCollector } from './DSLErrorCollector';
import { DataflowPreprocessor } from './grammar-with-annotation/dataflow-preprocessor';

const dataflowVisitor = require('./DataflowParserVisitor.js');

export const dataflowLanguageDeferred: any = import('./DataFlowLanguage').then((dataflowLanguage) => {
    Antlr4Utils.dataflowLexer = dataflowLanguage.dataflowLexer;
    Antlr4Utils.dataflowParser = dataflowLanguage.dataflowParser;
    Antlr4Utils.dataflowLexerWithAnnotation = dataflowLanguage.dataflowLexerWithAnnotation;
    Antlr4Utils.dataflowParserWithAnnotation = dataflowLanguage.dataflowParserWithAnnotation;
});

const antlr4 = require('antlr4');

export enum ParsingVersion {
    StableVersion = 'vStable',
    OriginalVersion = 'v1',
    EnhancedWithAnnotation = 'v2'
}

export class Antlr4Utils {

    public static enableDataflowAnnotation: boolean = false;

    public static dataflowLexer: any;
    public static dataflowParser: any;
    public static dataflowLexerWithAnnotation: any;
    public static dataflowParserWithAnnotation: any;

    public static dataflowLanguageDeferred = dataflowLanguageDeferred;

    public static getLexerDefinition(version: ParsingVersion = ParsingVersion.StableVersion): any {
        switch (version) {
            case ParsingVersion.OriginalVersion:
                return this.dataflowLexer;
            case ParsingVersion.EnhancedWithAnnotation:
                return this.dataflowLexerWithAnnotation;
            case ParsingVersion.StableVersion:
            default:
                return this.enableDataflowAnnotation ? this.dataflowLexerWithAnnotation : this.dataflowLexer;
        }
    }

    public static getParserDefinition(version: ParsingVersion = ParsingVersion.StableVersion): any {
        switch (version) {
            case ParsingVersion.OriginalVersion:
                return this.dataflowParser;
            case ParsingVersion.EnhancedWithAnnotation:
                return this.dataflowParserWithAnnotation;
            case ParsingVersion.StableVersion:
            default:
                return this.enableDataflowAnnotation ? this.dataflowParserWithAnnotation : this.dataflowParser;
        }
    }

    public static getVisitorDefinition(): any {
        return dataflowVisitor;
    }

    public static getTokenStream(script: string, errorListener?: any, version: ParsingVersion = ParsingVersion.StableVersion): any {
        switch (version) {
            case ParsingVersion.OriginalVersion:
                // noop
                break;
            case ParsingVersion.EnhancedWithAnnotation:
                script = new DataflowPreprocessor(script).result;
                break;
            case ParsingVersion.StableVersion:
            default:
                if (Antlr4Utils.enableDataflowAnnotation) {
                    script = new DataflowPreprocessor(script).result;
                }
                break;
        }
        return new antlr4.CommonTokenStream(Antlr4Utils.getLexer(script, errorListener, version));
    }

    public static getLexer(script: string, errorListener?: any, version: ParsingVersion = ParsingVersion.StableVersion): any {
        const stream = new antlr4.InputStream(script);
        const dataflowLexerDefinition = this.getLexerDefinition(version);
        const lexer = new dataflowLexerDefinition.DataflowLexer(stream);

        if (errorListener) {
            lexer.removeErrorListeners();
            lexer.addErrorListener(errorListener);
        }
        return lexer;
    }

    public static getParser(tokenStream: any, errorListener?: any, version: ParsingVersion = ParsingVersion.StableVersion): any {
        const dataflowParser = Antlr4Utils.getParserDefinition(version);
        const parser = new dataflowParser.DataflowParser(tokenStream, errorListener);

        if (errorListener) {
            parser.removeErrorListeners();
            parser.addErrorListener(errorListener);
        }
        return parser;
    }

    public static getExpression(tokenStream: any, errorListener?: any, version: ParsingVersion = ParsingVersion.StableVersion): any {
        // This two stage parsing method is recommended by ANTLR4 documentation when dealing with
        // slow parsing: https://github.com/antlr/antlr4/blob/master/doc/faq/general.md#why-is-my-expression-parser-slow
        // with more JS specific info here: https://github.com/antlr/antlr4/issues/2206

        const parser = Antlr4Utils.getParser(tokenStream, errorListener, version);
        parser._interp.predictionMode = antlr4.atn.PredictionMode.SLL;
        try {
            return parser.expression();
        } catch {
            tokenStream.reset();
            parser.reset();
            parser._interp.predictionMode = antlr4.atn.PredictionMode.LL;
            return parser.expression();
        }
    }

    public static getText(tokenStream: any, node: any): string {
        if (!node) {
            return undefined;
        }
        if (node.start && node.stop) {
            return tokenStream.getText(new antlr4.Interval(node.start, node.stop));
        }
        return node.getText();
    }

    public static hasSyntaxErrorsInExpression(expressionValue: string, version: ParsingVersion = ParsingVersion.StableVersion): boolean {
        if (!expressionValue) {
            return false;
        }
        const errorCollector = new DSLErrorCollector();
        const tokenStream = Antlr4Utils.getTokenStream(expressionValue, errorCollector, version);
        const expression = Antlr4Utils.getExpression(tokenStream, errorCollector, version);

        const dataflowParser = Antlr4Utils.getParserDefinition(version);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const interestingTokens = tokenStream.tokens.filter((token: any) => token.type !== dataflowParser.DataflowParser.WS && token.type !== -1);

        return (errorCollector.errors && errorCollector.errors.length > 0) || (interestingTokens.length > 0 && interestingTokens[interestingTokens.length - 1] !== expression.stop);
    }

}
